// Audio system. Keep all audio access funnelling through here so:
//  - missing files don't crash the game (loaderror is swallowed)
//  - BGM persists across scene transitions and cross-fades cleanly
//  - the page-level mute toggle (game.sound.mute) controls everything
//
// Drop MP3s into apps/web/public/audio/<key>.mp3. Filenames must match
// AUDIO_KEYS exactly. Any file you don't ship is silently skipped at
// load time, so you can ship music first and SFX later.

import Phaser from 'phaser';

export const AUDIO_KEYS = {
  bgmIntro: 'bgm_loadscreen',
  bgmMain: 'bgm_main',
  bgmBoss: 'bgm_boss',
  bgmEnd: 'bgm_end',

  sfxSearchFound: 'sfx_search_found',
  sfxSearchEmpty: 'sfx_search_empty',
  sfxScanStart: 'sfx_scan_start',
  sfxScanSafe: 'sfx_scan_safe',
  sfxScanExploitable: 'sfx_scan_exploitable',
  sfxPatch: 'sfx_patch',
  sfxAssetsSecured: 'sfx_assets_secured',
  sfxCveHit: 'sfx_cve_hit',
  sfxDash: 'sfx_dash',
  sfxBossIntro: 'sfx_boss_intro',
  sfxLose: 'sfx_lose',
  sfxClick: 'sfx_click',
} as const;

type AudioKey = typeof AUDIO_KEYS[keyof typeof AUDIO_KEYS];

const ALL_KEYS: AudioKey[] = Object.values(AUDIO_KEYS);

// Module-level state so BGM survives scene.start() handoffs.
let currentBgm: Phaser.Sound.BaseSound | null = null;
let currentBgmKey: AudioKey | null = null;

const DEFAULT_BGM_VOLUME = 0.45;
const DEFAULT_SFX_VOLUME = 0.7;

/**
 * Register every audio key with the loader. Missing files are caught and
 * skipped silently so the game still boots before all assets exist.
 */
export const loadAudio = (scene: Phaser.Scene): void => {
  // Catch only this loader's errors to avoid leaking handlers across scenes
  scene.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: Phaser.Loader.File) => {
    if (file.type === 'audio') {
      // expected: we don't fail boot when a track hasn't been added yet
      console.warn(`[audio] missing /audio/${file.key}.mp3 (skipping)`);
    }
  });
  for (const key of ALL_KEYS) {
    scene.load.audio(key, `/audio/${key}.mp3`);
  }
};

const has = (scene: Phaser.Scene, key: AudioKey): boolean => scene.cache.audio.exists(key);

/** Play a one-shot SFX, no-op if the file isn't present. */
export const playSfx = (
  scene: Phaser.Scene,
  key: AudioKey,
  volume = DEFAULT_SFX_VOLUME,
): void => {
  if (!has(scene, key)) return;
  scene.sound.play(key, { volume });
};

/**
 * Switch to a new BGM track. If the same key is already playing this is a
 * no-op. The previous track fades out and is destroyed; the new track starts
 * immediately at the target volume. If the audio context is still locked
 * (browser autoplay policy), Phaser queues the play call and runs it on the
 * first user gesture — that's why we can call this from BootScene/IntroScene
 * even before the user has clicked anything.
 */
export const playBgm = (
  scene: Phaser.Scene,
  key: AudioKey,
  volume = DEFAULT_BGM_VOLUME,
  fadeMs = 400,
): void => {
  if (currentBgmKey === key && currentBgm) return;
  if (!has(scene, key)) {
    console.warn(`[audio] BGM ${key} not loaded, skipping`);
    return;
  }

  // Fade out + destroy any previous track
  if (currentBgm) {
    const prev = currentBgm;
    scene.tweens.add({
      targets: prev,
      volume: 0,
      duration: fadeMs,
      ease: 'Linear',
      onComplete: () => {
        prev.stop();
        prev.destroy();
      },
    });
  }

  // Start the new track immediately at target volume
  const next = scene.sound.add(key, { loop: true, volume });
  next.play();

  currentBgm = next;
  currentBgmKey = key;
};

/**
 * Stop and destroy the current BGM (e.g. when reaching the End scene).
 * Synchronous — a fade tween could die mid-fade if the calling scene shuts
 * down (Replay button), leaving the sound orphan-looping forever and
 * doubling up when the new BGM starts.
 */
export const stopBgm = (_scene: Phaser.Scene): void => {
  if (!currentBgm) return;
  currentBgm.stop();
  currentBgm.destroy();
  currentBgm = null;
  currentBgmKey = null;
};

/**
 * Synthesise simple SFX for any key that didn't load from disk. Call this
 * from BootScene.create() (after the loader has completed). Uses Web Audio
 * OfflineAudioContext so it works without any MP3 files, matching the
 * procedural-sprite pattern. Drop-in MP3s take precedence — keys already
 * present in the cache are skipped.
 *
 * BGM keys are intentionally excluded: procedural looping music is out of
 * scope; the game works fine without background music.
 */
export const generateProceduralSounds = (scene: Phaser.Scene): void => {
  const SR = 44100;

  // Single oscillator note with a simple attack/release envelope.
  const addNote = (
    ctx: OfflineAudioContext,
    type: OscillatorType,
    freq: number,
    freqEnd: number,
    t: number,
    dur: number,
    vol: number,
  ) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.connect(g);
    g.connect(ctx.destination);
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (freqEnd !== freq) osc.frequency.linearRampToValueAtTime(freqEnd, t + dur);
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(vol, t + Math.min(0.012, dur * 0.1));
    g.gain.setValueAtTime(vol, t + dur * 0.55);
    g.gain.linearRampToValueAtTime(0, t + dur);
    osc.start(t);
    osc.stop(t + dur);
  };

  const make = (durS: number, setup: (ctx: OfflineAudioContext) => void): Promise<AudioBuffer> => {
    const ctx = new OfflineAudioContext(1, Math.ceil(SR * durS), SR);
    setup(ctx);
    return ctx.startRendering();
  };

  const builders: Partial<Record<AudioKey, () => Promise<AudioBuffer>>> = {
    [AUDIO_KEYS.sfxSearchFound]: () =>
      make(0.35, ctx => {
        addNote(ctx, 'sine', 440, 440, 0, 0.15, 0.45);
        addNote(ctx, 'sine', 660, 660, 0.17, 0.15, 0.45);
      }),

    [AUDIO_KEYS.sfxSearchEmpty]: () =>
      make(0.15, ctx => {
        addNote(ctx, 'sine', 120, 70, 0, 0.13, 0.4);
      }),

    [AUDIO_KEYS.sfxScanStart]: () =>
      make(0.3, ctx => {
        addNote(ctx, 'sine', 300, 800, 0, 0.25, 0.35);
      }),

    [AUDIO_KEYS.sfxScanSafe]: () =>
      make(0.28, ctx => {
        addNote(ctx, 'sine', 880, 880, 0, 0.09, 0.4);
        addNote(ctx, 'sine', 880, 880, 0.14, 0.09, 0.4);
      }),

    [AUDIO_KEYS.sfxScanExploitable]: () =>
      make(0.35, ctx => {
        addNote(ctx, 'square', 600, 200, 0, 0.3, 0.22);
      }),

    [AUDIO_KEYS.sfxPatch]: () =>
      make(0.2, ctx => {
        addNote(ctx, 'sine', 1000, 1000, 0, 0.15, 0.5);
        addNote(ctx, 'sine', 1500, 1500, 0, 0.08, 0.25);
      }),

    [AUDIO_KEYS.sfxAssetsSecured]: () =>
      make(0.5, ctx => {
        addNote(ctx, 'sine', 523, 523, 0, 0.13, 0.4);
        addNote(ctx, 'sine', 659, 659, 0.15, 0.13, 0.4);
        addNote(ctx, 'sine', 784, 784, 0.30, 0.18, 0.4);
      }),

    [AUDIO_KEYS.sfxCveHit]: () =>
      make(0.2, ctx => {
        addNote(ctx, 'sawtooth', 180, 50, 0, 0.15, 0.5);
      }),

    [AUDIO_KEYS.sfxDash]: () =>
      make(0.15, ctx => {
        // Layered detuned sawtooths approximate a swoosh.
        const g = ctx.createGain();
        g.connect(ctx.destination);
        g.gain.setValueAtTime(0.28, 0);
        g.gain.linearRampToValueAtTime(0, 0.13);
        [400, 700, 1100, 1600, 2200].forEach((f, i) => {
          const osc = ctx.createOscillator();
          osc.connect(g);
          osc.type = 'sawtooth';
          osc.frequency.setValueAtTime(f + i * 40, 0);
          osc.frequency.linearRampToValueAtTime(f * 1.5, 0.13);
          osc.start(0);
          osc.stop(0.13);
        });
      }),

    [AUDIO_KEYS.sfxBossIntro]: () =>
      make(0.5, ctx => {
        addNote(ctx, 'sine', 60, 35, 0, 0.4, 0.7);
        addNote(ctx, 'sine', 120, 70, 0, 0.25, 0.35);
      }),

    [AUDIO_KEYS.sfxLose]: () =>
      make(0.72, ctx => {
        addNote(ctx, 'sine', 440, 440, 0, 0.2, 0.4);
        addNote(ctx, 'sine', 330, 330, 0.24, 0.2, 0.4);
        addNote(ctx, 'sine', 220, 220, 0.48, 0.22, 0.4);
      }),

    [AUDIO_KEYS.sfxClick]: () =>
      make(0.06, ctx => {
        addNote(ctx, 'sine', 1200, 1200, 0, 0.045, 0.35);
      }),
  };

  for (const [key, build] of Object.entries(builders) as [AudioKey, () => Promise<AudioBuffer>][]) {
    if (has(scene, key)) continue; // real MP3 loaded — don't overwrite
    build().then(buf => {
      scene.cache.audio.add(key, buf);
    }).catch(() => {
      // OfflineAudioContext unavailable (headless tests etc.) — silent skip
    });
  }
};
